/**
 * The Studio's create-a-project step: name it, and choose which songs are in it.
 *
 * This is the screen Batch 6 §3 asks for — "when a user clicks the Studio navigation item,
 * do not immediately throw them into an empty workspace". Two decisions, in the order the
 * brief states them, and nothing else: a name, and ONE library song or ALL of them.
 *
 * WHY THE SCOPE IS A RADIO GROUP AND THE SONG IS A SELECT. They are two different kinds of
 * question. "One song or all of them" is a choice between two modes, so it is a radio group
 * — the shape RadioCard exists for. "Which one" is a pick from a list that is as long as
 * the library, so it is a select rather than a third radio per song, which on a
 * fifty-track library would be a screen of radios.
 *
 * The song select is present, not swapped in, and is DISABLED in the "all songs" mode
 * rather than unmounted: a control that appears and disappears as you change the mode above
 * it makes the button underneath move while you are reaching for it, and the disabled state
 * says what the mode did to it.
 *
 * NOTHING HERE CREATES OR STORES MUSIC. The options are the user's existing library, passed
 * in; a project holds ids. See the `StudioProject` type and api/studioProjectsApi.
 *
 * VALIDATION IS THE API'S RULE, passed in as `nameError` rather than re-implemented, so the
 * message this form shows and the refusal a server would give are one rule. See
 * api/studioProjectsApi.projectNameError.
 */
import { useId, useState } from 'react';
import { FolderPlus } from 'lucide-react';
import { RadioCard, RadioCardGroup, Select, TextField } from '../forms';
import { Btn } from '../ui';
import { c } from '../../styles/theme';
import type { ProjectSongScope, Song } from '../../types/domain';

export interface NewProjectFormProps {
  /** The user's library, as the options for the "one song" mode. */
  songs: Song[];
  /**
   * Why a name would be refused, or null when it is fine.
   *
   * The API's own validator, threaded down. Called on every keystroke, but only SHOWN
   * after the field has been touched — see `touched` below.
   */
  nameError: (name: string) => string | null;
  /**
   * Creates the project. Given the name, the chosen ids and the mode.
   *
   * The ids are resolved here rather than in the caller because this component is where
   * the mode lives: "all" means every song it was given, which is a fact about what is on
   * this screen.
   */
  onCreate: (name: string, songIds: string[], scope: ProjectSongScope) => void;
  /**
   * True when `songs` is empty because the library could not be read, not because it is
   * empty.
   *
   * WHAT IT CHANGES IS THE WORDING, NOT THE BEHAVIOUR. An unreadable library offers nothing
   * to pick from, so "One song" is still unavailable and the select is still disabled —
   * that part was already right. What was wrong is that both controls EXPLAINED themselves
   * as "No songs yet", and the count beside "All songs in my library" read "· 0 tracks",
   * which is a statement about the account rather than about the request. A user whose
   * forty tracks failed to load was told they had none, twice, on the screen where they
   * name a project. Optional, so the default is the honest reading of a plain empty
   * library; the caller decides, because it is the one that holds the read state.
   */
  libraryUnavailable?: boolean;
}

export default function NewProjectForm({
  songs,
  nameError,
  onCreate,
  libraryUnavailable = false,
}: NewProjectFormProps) {
  const nameId = useId();
  const songId = useId();
  /** The validation message's id, so the name field can point `aria-describedby` at it. */
  const errorId = `${nameId}-error`;

  const [name, setName] = useState('');
  /**
   * Whether the name field has been interacted with.
   *
   * Without this the form opens with "Name your project" already printed in red under an
   * untouched empty field, which reads as an error the user has made rather than as a
   * field they have not reached yet. The button is disabled from the start either way, so
   * nothing is hidden — only the accusation is deferred.
   */
  const [touched, setTouched] = useState(false);
  const [scope, setScope] = useState<ProjectSongScope>('all');
  /**
   * The chosen song in "one" mode.
   *
   * Defaults to the first of the library rather than to empty, so the mode is never in a
   * state where it is selected and has nothing selected — an empty select with a Create
   * button beside it invites a press that cannot do anything.
   */
  const [chosenId, setChosenId] = useState(songs[0]?.id ?? '');

  const problem = nameError(name);
  // An empty library still allows a project: it is a name waiting for work, which is a
  // real state the type documents. So the only thing that can block Create is the name.
  const ids = scope === 'all' ? songs.map((s) => s.id) : chosenId ? [chosenId] : [];

  function submit() {
    setTouched(true);
    if (problem) return;
    onCreate(name.trim(), ids, scope);
    setName('');
    setTouched(false);
  }

  return (
    <form
      onSubmit={(e) => {
        // A real <form>, so Enter in the name field submits — the gesture anyone typing a
        // name into a single field expects. preventDefault because there is nowhere to
        // post to; the handler is the submission.
        e.preventDefault();
        submit();
      }}
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 12,
        background: c.surface1,
        padding: 16,
        marginBottom: 18,
      }}
      aria-labelledby={`${nameId}-legend`}
    >
      <p
        id={`${nameId}-legend`}
        style={{
          fontFamily: 'Space Grotesk',
          fontWeight: 600,
          fontSize: 14,
          margin: '0 0 4px',
          color: c.text1,
        }}
      >
        Start a project
      </p>
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 14px' }}>
        Name what you are working on, then choose the tracks it covers. You can open the
        workspace on any of them afterwards.
      </p>

      <TextField
        id={nameId}
        label="Project name"
        value={name}
        onChange={(value) => {
          setName(value);
          setTouched(true);
        }}
        placeholder="e.g. Summer EP"
        marginBottom={touched && problem ? 6 : 14}
        /*
          THE MESSAGE BELOW, TIED TO THE FIELD IT IS ABOUT. It was a paragraph after the
          input with no relationship to it: a screen reader user who moved back to the field
          to fix the name heard the label and the value and nothing about the refusal, and
          the field itself gave no indication of being the invalid one. TrimPanel does the
          same two attributes by hand on its raw inputs; this is that, through the shared
          field. See the props' docs in components/forms/TextField.tsx.
        */
        invalid={Boolean(touched && problem)}
        describedBy={touched && problem ? errorId : undefined}
      />
      {touched && problem && (
        // A live region, so the reason arrives for a screen reader as it appears rather
        // than only being found by someone who goes looking for it. The id is what the
        // field's aria-describedby points at, so it is also read on the field.
        <p id={errorId} role="alert" style={{ fontSize: 11, color: c.danger, margin: '0 0 14px' }}>
          {problem}
        </p>
      )}

      <RadioCardGroup label="Which songs" gap={6} marginBottom={12}>
        <RadioCard
          name={`${nameId}-scope`}
          value="all"
          checked={scope === 'all'}
          onSelect={() => setScope('all')}
          size="compact"
          ariaLabel="All songs in my library"
        >
          <span style={{ fontSize: 12 }}>
            All songs in my library
            {/*
              text2, because this is the count — the one thing on the card that says what
              "all" comes to for this account, and a 1-track library and a 40-track one
              choose differently. Secondary to the label beside it, which is what text2 is
              for; text3 would fade a number somebody is reading to decide.
            */}
            <span style={{ color: c.text2 }}>
              {libraryUnavailable
                ? // NOT "· 0 tracks". The count is unknown, and printing zero would be this
                  // form answering a question only the library read can answer.
                  ' · track count unavailable'
                : ` · ${songs.length} ${songs.length === 1 ? 'track' : 'tracks'}`}
            </span>
          </span>
        </RadioCard>
        <RadioCard
          name={`${nameId}-scope`}
          value="one"
          checked={scope === 'one'}
          onSelect={() => setScope('one')}
          size="compact"
          ariaLabel="One song"
          // Nothing to pick from, so the mode that requires a pick is not offered. The
          // "all songs" mode above still works on an empty library — it means "all
          // none of them", which is the name-waiting-for-work case.
          style={songs.length === 0 ? { opacity: 0.6 } : undefined}
        >
          <span style={{ fontSize: 12 }}>One song</span>
        </RadioCard>
      </RadioCardGroup>

      <Select
        id={songId}
        label="Song"
        value={chosenId}
        onChange={setChosenId}
        options={songs.map((s) => ({ value: s.id, label: s.title }))}
        placeholder={
          songs.length > 0
            ? undefined
            : libraryUnavailable
              ? // The disabled control says WHY it is disabled, which is the whole job of a
                // disabled control's label — and "No songs yet" was the wrong reason.
                'Library unavailable'
              : 'No songs yet'
        }
        disabled={scope !== 'one' || songs.length === 0}
        marginBottom={14}
      />

      <Btn type="submit" disabled={Boolean(problem)}>
        <FolderPlus size={14} />
        Create project
      </Btn>
    </form>
  );
}
